/**
 * API service for tenant i18n term overrides.
 */
import { apiGet, apiPut, apiDelete } from '../../../shared/api/axiosInstance';

interface SingleResponse<T> {
  data: T;
}

/** `'m' | 'f' | 'n'` — 'n' is "this language has no grammatical gender" (en, zh). */
export type I18nTermGender = 'm' | 'f' | 'n';

export interface I18nTermDefaults {
  singular: string;
  plural: string;
  gender: I18nTermGender;
}

export interface I18nTermOverride {
  id: string;
  tenant_id: string;
  locale: string;
  term_key: string;
  singular: string;
  plural: string;
  singular_lower: string;
  plural_lower: string;
  /** null = not declared; the config default for this term + locale is inherited. */
  gender: I18nTermGender | null;
  is_manual_edit: boolean;
  source_locale: string | null;
}

export interface I18nTermConfig {
  key: string;
  description: string;
  defaults: Record<string, I18nTermDefaults>;
  overrides: Record<string, I18nTermOverride>;
}

export interface I18nLoaderData {
  terms: Record<string, string>;
  overrides: Array<{ string_key: string; override_value: string }>;
}

export interface I18nTermSavePayload {
  term_key: string;
  locale: string;
  singular: string;
  plural: string;
  gender?: I18nTermGender;
}

export const i18nOverridesApi = {
  /** Fetch combined i18n data for frontend loader (any authenticated user) */
  getLoaderData: async (locale: string): Promise<I18nLoaderData> => {
    const response = await apiGet<SingleResponse<I18nLoaderData>>(`/api/v1/settings/i18n?locale=${encodeURIComponent(locale)}`);
    return response.data;
  },

  /** Fetch all curated terms with defaults and overrides (admin) */
  getAllTerms: async (): Promise<I18nTermConfig[]> => {
    const response = await apiGet<SingleResponse<I18nTermConfig[]>>('/api/v1/settings/i18n-terminology');
    return response.data;
  },

  /** Save a curated term override (admin) */
  saveTerm: async (payload: I18nTermSavePayload): Promise<I18nTermOverride> => {
    const response = await apiPut<SingleResponse<I18nTermOverride>>('/api/v1/settings/i18n-terminology', payload);
    return response.data;
  },

  /** Delete a curated term override (admin) */
  deleteTerm: async (termKey: string, locale: string): Promise<void> => {
    await apiDelete(`/api/v1/settings/i18n-terminology/${termKey}?locale=${encodeURIComponent(locale)}`);
  },
};
